import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Typography,
    Alert,
    Box
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { post } from '../requests';
import { useMessage } from '../hooks';

export function YandexTokenDialog({ open, onClose, onTokenSet }) {
    const { t } = useTranslation();
    const { showError, showSuccess } = useMessage();
    const [token, setToken] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        if (open) {
            setToken('');
            setError('');
        }
    }, [open]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setLoading(true);

        try {
            await post('/api/v1/user_token', { yandex_token: token });
            // Also save token to localStorage for settings
            localStorage.setItem('authToken', token);
            showSuccess(t('notifications:yandex_token_saved', 'Yandex token saved successfully'));
            if (onTokenSet) {
                onTokenSet(token);
            }
            // Close dialog and reload page to continue with authorization
            onClose();
            // Small delay to ensure token is saved before reload
            setTimeout(() => {
                window.location.reload();
            }, 500);
        } catch (err) {
            const errorMessage = err.message || t('notifications:error_saving_token', 'Failed to save token');
            setError(errorMessage);
            showError(errorMessage);
            setLoading(false);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <form onSubmit={handleSubmit}>
                <DialogTitle>
                    <Typography variant="h6">
                        {t('components:yandex_token_dialog.title', 'Link Yandex Token')}
                    </Typography>
                </DialogTitle>
                <DialogContent>
                    <Box sx={{ mt: 2 }}>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                            {t('components:yandex_token_dialog.description', 
                                'To continue, you need to link your Yandex Tracker token. Please enter your OAuth token from Yandex.')}
                        </Typography>
                        
                        {error && (
                            <Alert severity="error" sx={{ mb: 2 }}>
                                {error}
                            </Alert>
                        )}

                        <TextField
                            autoFocus
                            margin="dense"
                            label={t('components:yandex_token_dialog.token_label', 'Yandex OAuth Token')}
                            type="password"
                            fullWidth
                            variant="outlined"
                            value={token}
                            onChange={(e) => setToken(e.target.value)}
                            required
                            disabled={loading}
                            helperText={t('components:yandex_token_dialog.helper_text', 
                                'Enter your Yandex OAuth token. This token will be securely stored and used for API requests.')}
                        />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={onClose} disabled={loading}>
                        {t('common:cancel', 'Cancel')}
                    </Button>
                    <Button type="submit" variant="contained" disabled={loading || !token.trim()}>
                        {loading 
                            ? t('common:saving', 'Saving...') 
                            : t('common:save', 'Save')}
                    </Button>
                </DialogActions>
            </form>
        </Dialog>
    );
}

